import { useFlags as useLDFlags } from "launchdarkly-react-client-sdk-original";
import sortBy from "lodash-es/sortBy";

import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Toggle from "ds/components/Toggle";
import TooltipInfoFloating from "ds/components/TooltipInfo";
import Typography from "ds/components/Typography";

import PollingFlag from "./PollingFlag";
import { useFlags, useModifiedFlags } from "./useFlags";

export function FlagsList() {
  const [lsFlags, modifyFlag, clearFlags] = useModifiedFlags();
  const ldFlags = useLDFlags();
  const finalFlags = useFlags();

  const handleChange = (id: string, checked: boolean) => {
    modifyFlag(id, checked);
  };

  const list = sortBy(Object.entries(finalFlags), ([key]) => key).filter(
    ([, value]) => typeof value === "boolean"
  );
  const modifiedFlags = Object.entries(lsFlags).map(([key]) => key);

  return (
    <Box direction="column" gap="large" align="start">
      <Banner
        variant="info"
        title="Flags are modified only on the client side"
        fullWidth
        storageKey="devtools-flags-info"
      >
        These changes are not saved in LaunchDarkly, so modifying backend flags will not have any
        effect.
      </Banner>
      <Box grid gridTemplate="1fr 1fr" gap="medium">
        {list.map(([key, value]) => (
          <Toggle
            key={key}
            variant="checkbox"
            onChange={(isSelected) => handleChange(key, isSelected)}
            isSelected={value as boolean}
            id={key}
          >
            <Typography tag="span" variant={modifiedFlags.includes(key) ? "p-t7" : "p-body3"}>
              {key}
            </Typography>
            {modifiedFlags.includes(key) && (
              <TooltipInfoFloating widthMode="maxWidthSm">
                This flag was modified in the devtools and it is out of sync with the original value
                in LaunchDarkly. The original value is set to: {ldFlags[key] ? "on" : "off"}
              </TooltipInfoFloating>
            )}
          </Toggle>
        ))}
      </Box>
      <PollingFlag modifyFlag={modifyFlag} finalFlags={ldFlags} />
      {modifiedFlags.length > 0 && (
        <Button variant="dangerSecondary" size="small" onPress={clearFlags}>
          Clear modified flags
        </Button>
      )}
    </Box>
  );
}
